'use client';

import { useContext } from 'react';
import { TabsContext } from './TabsContext';
import { TabsVariant, TabProps, cn } from 'types';

const TAB_VARIANT_STYLES = {
  [TabsVariant.NORMAL]: {
    base: 'pb-4 border-b-[3px]',
    active: 'border-turquoise',
    inactive: 'border-neutralGrey text-neutralPlus hover:border-turquoise hover:text-black',
    disabled: 'text-neutralGrey',
  },
  [TabsVariant.UNDERLINE]: {
    base: 'px-4 py-3 rounded-t-sm',
    active: 'border-b-[4px] border-turquoise z-2 -mb-1',
    inactive: 'hover:bg-turquoise',
    disabled: 'text-neutralGrey',
  },
  [TabsVariant.FILLED]: {
    base: 'py-2 px-6 rounded-sm',
    active: 'bg-white border-b-[4px] border-turquoise',
    inactive: 'hover:bg-turquoise',
    disabled: 'text-neutralPlus',
  },
} as const;

export function Tab(props: TabProps) {
  const { children, tabId, className, onTabClick, isActive, disabled } = props;
  const { selectedTabId, setSelectedTabId, variant } = useContext(TabsContext);

  const isSelected = selectedTabId === tabId || isActive;

  const handleTabClick = () => {
    if (disabled) return;
    setSelectedTabId(tabId);
    onTabClick?.();
  };

  const currentVariantStyles = TAB_VARIANT_STYLES[variant];
  const tabState = disabled ? 'disabled' : isSelected ? 'active' : 'inactive';
  const fontWeight = isSelected ? 'font-extrabold' : 'font-semibold';

  return (
    <li
      className={cn(
        'cursor-pointer text-black',
        fontWeight,
        { 'pointer-events-none': disabled },
        currentVariantStyles.base,
        currentVariantStyles[tabState],
        className
      )}
      onClick={handleTabClick}
      role="tab"
      aria-selected={isSelected}
      aria-disabled={disabled}
    >
      <>{children}</>
    </li>
  );
}

Tab.displayName = 'Tabs.Tab';
